import {
  Fragment,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
} from 'react-leaflet';
import { DivIcon, LatLngBounds, LatLngExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import {
  FitBounds,
  BoundsListener,
} from '../../components/MapBoundsComponents';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUpdateSession } from '../../hooks/useSessions';
import { useRoutesForManagement } from '../../hooks/useRoutes';
import { RouteWithGates } from '../../api/routes';
import { Gate } from '../../types/Gate';
import { createGateIcon } from '../../utils/gateMapUtils';
import markerIconPng from '../../icons/marker-icon-green.png';
import endMarkerIconPng from '../../icons/marker-icon-red.png';
import toast from 'react-hot-toast';
import { clusterRoutes } from '../../utils/routeClustering';
import {
  HiOutlineChevronRight,
} from 'react-icons/hi2';

import {
  NEW_SESSION_ROUTE_KEY,
} from './routePickerConstants';

type RoutePickerState = {
  returnTo?: string;
  sessionId: string;
  isNewSession?: boolean;
};

const FALLBACK_CENTER: LatLngExpression = [51.5074, -0.1278];

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

function createRouteIcon(label: string): DivIcon {
  return new DivIcon({
    className: '',
    html: `
      <div style="position:relative;display:inline-flex;
        align-items:center;justify-content:center;">
        <div style="width:28px;height:28px;border-radius:50%;
          background:#1d4ed8;border:2px solid #fff;
          display:flex;align-items:center;justify-content:center;
          color:#fff;font-size:12px;font-weight:700;
          box-shadow:0 1px 3px rgba(0,0,0,0.3);">${label}</div>
      </div>
    `,
    iconAnchor: [14, 14],
    popupAnchor: [0, -18],
  });
}

export default function RoutePicker(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const routeState = location.state as RoutePickerState | null;
  const returnTo = routeState?.returnTo;
  const sessionId = routeState?.sessionId ?? '';
  const isNewSession = routeState?.isNewSession ?? false;

  const updateSession = useUpdateSession(sessionId);
  const {
    data: routes,
    isPending,
    isError,
  } = useRoutesForManagement();

  const [phase, setPhase] = useState<'routes' | 'gates'>('routes');
  const [selectedRoute, setSelectedRoute] = useState<
    RouteWithGates | null
  >(null);
  const [activeRouteId, setActiveRouteId] = useState<string | null>(
    null,
  );
  const [activeGateId, setActiveGateId] = useState<string | null>(
    null,
  );
  const [fitTrigger, setFitTrigger] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [focusBounds, setFocusBounds] = useState<
    LatLngBounds | null
  >(null);
  const [expandedClusters, setExpandedClusters] = useState<
    Set<number>
  >(new Set());

  const clusteredRoutes = useMemo(
    () => clusterRoutes(routes ?? []),
    [routes],
  );

  // Auto-expand when there's only one cluster
  const effectiveClusters = useMemo(() => {
    if (clusteredRoutes.length === 1) {
      return new Set([clusteredRoutes[0].clusterId]);
    }
    return expandedClusters;
  }, [clusteredRoutes, expandedClusters]);

  const toggleCluster = useCallback((id: number) => {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // --- Phase 1: Routes ---

  const routeMarkers = useMemo(() => {
    if (!routes) return [];
    return routes
      .map((r, i) => {
        const lat = r.origin_gates_lat?.[0];
        const lon = r.origin_gates_lon?.[0];
        if (lat == null || lon == null) return null;
        return {
          route: r,
          position: [lat, lon] as LatLngExpression,
          order: i + 1,
        };
      })
      .filter(Boolean) as {
      route: RouteWithGates;
      position: LatLngExpression;
      order: number;
    }[];
  }, [routes]);

  const routeBounds = useMemo(() => {
    if (!routeMarkers.length) return null;
    return new LatLngBounds(routeMarkers.map((m) => m.position));
  }, [routeMarkers]);

  const routeMapCenter = useMemo(() => {
    if (routeBounds) return routeBounds.getCenter();
    return FALLBACK_CENTER;
  }, [routeBounds]);

  // --- Phase 2: Gates on selected route ---

  const gateMarkers = useMemo(() => {
    if (!selectedRoute) return [];
    return selectedRoute.gates
      .map((g, i) => {
        const lat = g.gates_lat?.[0];
        const lon = g.gates_lon?.[0];
        if (lat == null || lon == null) return null;
        const endLat = g.gates_lat?.[1];
        const endLon = g.gates_lon?.[1];
        const end =
          endLat != null && endLon != null
            ? ([endLat, endLon] as LatLngExpression)
            : null;
        return {
          gate: g,
          start: [lat, lon] as LatLngExpression,
          end,
          order: i + 1,
        };
      })
      .filter(Boolean) as {
      gate: Gate;
      start: LatLngExpression;
      end: LatLngExpression | null;
      order: number;
    }[];
  }, [selectedRoute]);

  const gateBounds = useMemo(() => {
    if (!gateMarkers.length) return null;
    const pts: LatLngExpression[] = [];
    gateMarkers.forEach((m) => {
      pts.push(m.start);
      if (m.end) pts.push(m.end);
    });
    return new LatLngBounds(pts);
  }, [gateMarkers]);

  const gateMapCenter = useMemo(() => {
    if (gateBounds) return gateBounds.getCenter();
    return FALLBACK_CENTER;
  }, [gateBounds]);

  const getGateIcon = useCallback(
    (type: 'start' | 'end', label: string) =>
      createGateIcon(type, label, markerIconPng, endMarkerIconPng),
    [],
  );

  // --- Handlers ---

  const navigateBack = useCallback(() => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate(-1);
    }
  }, [navigate, returnTo]);

  const storeNewSessionSelection = useCallback(
    (routeId: string, routeDesc: string,
      gateId?: string, gateDesc?: string) => {
      sessionStorage.setItem(NEW_SESSION_ROUTE_KEY, JSON.stringify({
        routeId, routeDescription: routeDesc,
        gateId: gateId ?? null,
        gateDescription: gateDesc ?? null,
      }));
      toast.success(
        gateId ? 'Route and gate selected' : 'Route selected',
      );
      navigateBack();
    },
    [navigateBack],
  );

  const routeLabel = (r: RouteWithGates) =>
    r.address || r.description || r.id;

  const gateLabel = (g: Gate) =>
    g.address?.trim() || g.notes?.trim() || g.id;

  const handleSelectRoute = useCallback(
    async (route: RouteWithGates) => {
      if (isSaving) return;
      if (isNewSession) {
        if (route.gates.length <= 1) {
          const gate = route.gates[0];
          storeNewSessionSelection(
            route.id, routeLabel(route),
            gate?.id, gate ? gateLabel(gate) : undefined,
          );
        } else {
          setSelectedRoute(route);
          setPhase('gates');
          setActiveGateId(null);
          setFitTrigger((t) => t + 1);
        }
        return;
      }
      if (route.gates.length === 0) {
        setIsSaving(true);
        try {
          await updateSession.mutateAsync({ route_id: route.id });
          toast.success('Route assigned');
          navigateBack();
        } catch {
          toast.error('Failed to assign route');
        } finally {
          setIsSaving(false);
        }
        return;
      }
      if (route.gates.length === 1) {
        setIsSaving(true);
        try {
          await updateSession.mutateAsync({
            route_id: route.id,
            gates_id: route.gates[0].id,
          });
          toast.success('Route and gate assigned');
          navigateBack();
        } catch {
          toast.error('Failed to assign route and gate');
        } finally {
          setIsSaving(false);
        }
        return;
      }
      setSelectedRoute(route);
      setPhase('gates');
      setActiveGateId(null);
      setFitTrigger((t) => t + 1);
    },
    [isSaving, isNewSession, updateSession, navigateBack,
      storeNewSessionSelection],
  );

  const handleSelectGate = useCallback(
    async (gate: Gate) => {
      if (isSaving || !selectedRoute) return;
      if (isNewSession) {
        storeNewSessionSelection(
          selectedRoute.id, routeLabel(selectedRoute),
          gate.id, gateLabel(gate),
        );
        return;
      }
      setIsSaving(true);
      try {
        await updateSession.mutateAsync({
          route_id: selectedRoute.id,
          gates_id: gate.id,
        });
        toast.success('Route and gate assigned');
        navigateBack();
      } catch {
        toast.error('Failed to assign gate');
      } finally {
        setIsSaving(false);
      }
    },
    [isSaving, isNewSession, selectedRoute, updateSession,
      navigateBack, storeNewSessionSelection],
  );

  const handleCenterRoute = useCallback(
    (route: RouteWithGates) => {
      const pts: LatLngExpression[] = [];
      const lat0 = route.origin_gates_lat?.[0];
      const lon0 = route.origin_gates_lon?.[0];
      if (lat0 != null && lon0 != null) {
        pts.push([lat0, lon0]);
      }
      for (const g of route.gates) {
        const gLat = g.gates_lat?.[0];
        const gLon = g.gates_lon?.[0];
        if (gLat != null && gLon != null) {
          pts.push([gLat, gLon]);
        }
        const gLat2 = g.gates_lat?.[1];
        const gLon2 = g.gates_lon?.[1];
        if (gLat2 != null && gLon2 != null) {
          pts.push([gLat2, gLon2]);
        }
      }
      if (pts.length === 0) return;
      setFocusBounds(new LatLngBounds(pts));
      setFitTrigger((t) => t + 1);
      setActiveRouteId(route.id);
    },
    [],
  );

  const handleBackToRoutes = () => {
    setPhase('routes');
    setSelectedRoute(null);
    setActiveGateId(null);
    setFocusBounds(null);
    setFitTrigger((t) => t + 1);
  };

  const backLink = phase === 'gates'
    ? { to: '#', text: 'Back to Routes' }
    : returnTo
      ? { to: returnTo, text: 'Back to Session' }
      : undefined;

  const handleBackClick = phase === 'gates'
    ? handleBackToRoutes
    : undefined;

  // --- Render ---

  const isLoading = isPending || isSaving;
  const title = phase === 'routes'
    ? 'Select Route'
    : `Gates on: ${selectedRoute?.address || selectedRoute?.description || 'Route'}`;

  return (
    <div className="cr-w-full">
      <SectionHeader
        title={title}
        isLoading={isLoading}
        backLink={
          handleBackClick
            ? undefined
            : backLink
        }
        actions={
          handleBackClick ? (
            <button
              type="button"
              onClick={handleBackClick}
              className="cr-text-sm cr-text-blue-600 hover:cr-underline"
            >
              &larr; Back to Routes
            </button>
          ) : undefined
        }
      />

      <div className="cr-mt-6 cr-grid cr-gap-6">
        {/* Map */}
        <div className="cr-relative cr-h-[500px] cr-overflow-hidden cr-rounded-xl cr-bg-white cr-shadow-sm">
          {isPending && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
              <Spinner isFetching />
            </div>
          )}
          {isError && !isPending && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-red-600">
              Unable to load routes. Please try again later.
            </div>
          )}

          {/* Phase 1: Route markers */}
          {!isError && !isPending && phase === 'routes' && (
            <>
              {routeMarkers.length === 0 && (
                <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
                  No routes available for this team.
                </div>
              )}
              {routeMarkers.length > 0 && (
                <MapContainer
                  center={routeMapCenter}
                  zoom={13}
                  scrollWheelZoom
                  className="cr-h-full cr-w-full"
                >
                  <FitBounds
                    bounds={focusBounds ?? routeBounds}
                    trigger={fitTrigger}
                  />
                  <BoundsListener onBoundsChange={noop} />
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {routeMarkers.map((m) => (
                    <Marker
                      key={m.route.id}
                      position={m.position}
                      icon={createRouteIcon(String(m.order))}
                      eventHandlers={{
                        click: () => setActiveRouteId(m.route.id),
                      }}
                    >
                      <Popup>
                        <div className="cr-min-w-[180px]">
                          <p className="cr-font-semibold">
                            {m.route.address || m.route.description}
                          </p>
                          <p className="cr-text-xs cr-text-gray-500">
                            {m.route.gate_count} gate(s),{' '}
                            {m.route.total_session_count} session(s)
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              )}
            </>
          )}

          {/* Phase 2: Gate markers */}
          {!isError && !isPending && phase === 'gates' && (
            <>
              {gateMarkers.length === 0 && (
                <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
                  No gates on this route.
                </div>
              )}
              {gateMarkers.length > 0 && (
                <MapContainer
                  center={gateMapCenter}
                  zoom={15}
                  scrollWheelZoom
                  className="cr-h-full cr-w-full"
                >
                  <FitBounds
                    bounds={gateBounds}
                    trigger={fitTrigger}
                  />
                  <BoundsListener onBoundsChange={noop} />
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {gateMarkers.map((m) => (
                    <Fragment key={m.gate.id}>
                      <Marker
                        position={m.start}
                        icon={getGateIcon(
                          'start',
                          String(m.order),
                        )}
                      >
                        <Popup>
                          <div className="cr-min-w-[160px]">
                            <p className="cr-font-semibold">
                              Gate #{m.order}
                            </p>
                            <p className="cr-text-xs cr-text-gray-500">
                              Start
                            </p>
                            {m.gate.route_distance != null && (
                              <p className="cr-text-xs cr-text-gray-500">
                                Distance:{' '}
                                {m.gate.route_distance.toFixed(0)}m
                              </p>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                      {m.end && (
                        <Marker
                          position={m.end}
                          icon={getGateIcon(
                            'end',
                            String(m.order),
                          )}
                        >
                          <Popup>
                            <div className="cr-min-w-[160px]">
                              <p className="cr-font-semibold">
                                Gate #{m.order}
                              </p>
                              <p className="cr-text-xs cr-text-gray-500">
                                End
                              </p>
                            </div>
                          </Popup>
                        </Marker>
                      )}
                    </Fragment>
                  ))}
                </MapContainer>
              )}
            </>
          )}
        </div>

        {/* List */}
        <div className="cr-rounded-xl cr-bg-white cr-p-6 cr-shadow-sm">
          {phase === 'routes' && (
            <>
              <h2 className="cr-mb-2 cr-text-lg cr-font-semibold">
                Routes
              </h2>
              <p className="cr-mb-4 cr-text-sm cr-text-gray-600">
                Select a route to assign to this session.
              </p>
              <div className="cr-flex cr-flex-col cr-gap-2">
                {clusteredRoutes.map((cluster) => {
                  const isOpen = effectiveClusters.has(
                    cluster.clusterId,
                  );
                  return (
                    <div
                      key={cluster.clusterId}
                      className={
                        'cr-overflow-hidden cr-rounded-lg '
                        + 'cr-border cr-border-gray-200'
                      }
                    >
                      <button
                        type="button"
                        onClick={() =>
                          toggleCluster(cluster.clusterId)
                        }
                        className={
                          'cr-flex cr-w-full cr-items-center '
                          + 'cr-gap-2 cr-bg-gray-50 cr-p-4 '
                          + 'cr-transition-colors '
                          + 'hover:cr-bg-gray-100'
                        }
                      >
                        <HiOutlineChevronRight
                          className={[
                            'cr-h-5 cr-w-5 cr-text-gray-500',
                            'cr-transition-transform',
                            isOpen ? 'cr-rotate-90' : '',
                          ].join(' ')}
                        />
                        <span className={
                          'cr-text-lg cr-font-semibold'
                        }>
                          {cluster.clusterName}
                        </span>
                        <span className={
                          'cr-text-sm cr-text-gray-500'
                        }>
                          ({cluster.routes.length} route
                          {cluster.routes.length !== 1
                            ? 's' : ''})
                        </span>
                      </button>
                      {isOpen && (
                        <div className={
                          'cr-flex cr-flex-col cr-gap-2 '
                          + 'cr-border-t cr-border-gray-200 '
                          + 'cr-p-3'
                        }>
                          {cluster.routes.map((route) => {
                            const isActive =
                              route.id === activeRouteId;
                            return (
                              <div
                                key={route.id}
                                onClick={() =>
                                  setActiveRouteId(route.id)
                                }
                                className={[
                                  'cr-flex cr-cursor-pointer',
                                  'cr-items-center',
                                  'cr-justify-between',
                                  'cr-rounded-lg cr-border',
                                  'cr-p-4',
                                  'cr-transition-colors',
                                  isActive
                                    ? 'cr-border-cirrusBlue-200'
                                      + ' cr-bg-cirrusBlue-25'
                                    : 'cr-border-gray-200',
                                ].join(' ')}
                              >
                                <div className={
                                  'cr-flex cr-items-start '
                                  + 'cr-gap-3'
                                }>
                                  <div>
                                    <p className={
                                      'cr-font-medium'
                                    }>
                                      {route.address
                                        || route.description}
                                    </p>
                                    {route.address
                                      && route.description && (
                                      <p className={
                                        'cr-text-xs '
                                        + 'cr-text-gray-500'
                                      }>
                                        {route.description}
                                      </p>
                                    )}
                                    <p className={
                                      'cr-text-xs '
                                      + 'cr-text-gray-500'
                                    }>
                                      {route.gate_count} gate(s)
                                      {' \u00B7 '}
                                      {route.total_session_count}
                                      {' session(s)'}
                                    </p>
                                  </div>
                                </div>
                                <div className={
                                  'cr-flex cr-gap-2'
                                }>
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCenterRoute(
                                        route,
                                      );
                                    }}
                                  >
                                    Center
                                  </Button>
                                  <Button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectRoute(
                                        route,
                                      );
                                    }}
                                    disabled={isSaving}
                                  >
                                    Select
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {phase === 'gates' && selectedRoute && (
            <>
              <h2 className="cr-mb-2 cr-text-lg cr-font-semibold">
                Select a Gate
              </h2>
              <p className="cr-mb-4 cr-text-sm cr-text-gray-600">
                This route has {selectedRoute.gates.length} gates.
                Choose one to assign.
              </p>
              <div className="cr-flex cr-flex-col cr-gap-3">
                {gateMarkers.map((m) => {
                  const isActive = m.gate.id === activeGateId;
                  const label =
                    m.gate.address?.trim() ||
                    m.gate.notes?.trim() ||
                    m.gate.id;
                  return (
                    <div
                      key={m.gate.id}
                      onClick={() => setActiveGateId(m.gate.id)}
                      className={[
                        'cr-flex cr-cursor-pointer cr-items-center',
                        'cr-justify-between cr-rounded-lg cr-border',
                        'cr-p-4 cr-transition-colors',
                        isActive
                          ? 'cr-border-cirrusBlue-200 cr-bg-cirrusBlue-25'
                          : 'cr-border-gray-200',
                      ].join(' ')}
                    >
                      <div className="cr-flex cr-items-start cr-gap-3">
                        <span className="cr-flex cr-h-6 cr-w-6 cr-shrink-0 cr-items-center cr-justify-center cr-rounded-full cr-bg-cirrusBlue-100 cr-text-xs cr-font-semibold cr-text-white">
                          {m.order}
                        </span>
                        <div>
                          <p className="cr-font-medium">{label}</p>
                          {m.gate.route_distance != null && (
                            <p className="cr-text-xs cr-text-gray-500">
                              Distance:{' '}
                              {m.gate.route_distance.toFixed(0)}m
                            </p>
                          )}
                          {m.gate.route_bearing != null && (
                            <p className="cr-text-xs cr-text-gray-500">
                              Bearing:{' '}
                              {(m.gate.route_bearing < 0
                                ? m.gate.route_bearing + 360
                                : m.gate.route_bearing
                              ).toFixed(0)}
                              &deg;
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectGate(m.gate);
                        }}
                        disabled={isSaving}
                      >
                        Select
                      </Button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
